/* eslint-disable */
// @ts-nocheck
import {
  ClipboardIcon,
  PaletteIcon,
  ScalesIcon,
  CheckIcon,
  CheckCircleIcon,
  WarningIcon,
  MagnifyingGlassIcon,
  LockIcon,
  CellSignalFullIcon,
  CellTowerIcon,
  ChartBarIcon,
  ChartLineUpIcon,
  ProhibitInsetIcon,
  WarningCircleIcon,
  CheckSquareIcon,
  ClockIcon,
  FileMagnifyingGlassIcon,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Callout,
  Code,
  Flex,
  Grid,
  Progress,
  Text,
} from "@radix-ui/themes";
import { createRoute, useNavigate, useSearch } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as Logo } from "../../images/Logo-blue.svg";

import { rootRoute } from "../root/route";
import { z } from "zod";
import { useResearchProgress } from "../../utils/queries/brandcheck";
import { resultsRoute } from "./results";

interface ScanMessage {
  id: string;
  text: string;
  timestamp: number;
}

interface ResultMetric {
  id: string;
  label: string;
  value: string;
  color: "red" | "yellow" | "green";
}

type ProcessingStage = "gathering" | "scanning" | "results";

const ThinkingMessage = ({ text }: { text: string }) => {
  if (text.includes("[INFO]")) {
    return (
      <Code size="2" variant="ghost" className="text-puntt-accent-11">
        ✅{text}
      </Code>
    );
  }
  if (text.includes("[ALERT]")) {
    return (
      <Code size="2" variant="ghost" className="text-puntt-red-11">
        ⚠️
        {text}
      </Code>
    );
  }
  if (text.includes("[PREVIEW]")) {
    return (
      <Code size="2" variant="ghost" className="text-puntt-green-11">
        🔍
        {text}
      </Code>
    );
  }
  if (text.includes("[LOCKED]")) {
    return (
      <Code size="2" variant="ghost" className="text-puntt-green-11">
        🔒
        {text}
      </Code>
    );
  }
  return (
    <Code size="2" variant="ghost" className="text-puntt-green-11">
      {text}
    </Code>
  );
};
export const processRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sup-processing",
  validateSearch(search: Record<string, unknown>) {
    return z
      .object({
        sessionId: z.string().optional(),
      })
      .parse(search);
  },
  component: function ProcessComponent() {
    const [currentStage, setCurrentStage] =
      useState<ProcessingStage>("gathering");
    const [checklist, setChecklist] = useState<boolean[]>([]);
    const [scanMessages, setScanMessages] = useState<ScanMessage[]>([]);
    const [results, setResults] = useState<ResultMetric[]>([]);
    const [llmThoughts, setLlmThoughts] = useState<ScanMessage[]>([]);
    const [isLlmThinking, setIsLlmThinking] = useState<boolean>(false);
    const [stageCompleted, setStageCompleted] = useState<{
      [key in ProcessingStage]: boolean;
    }>({
      gathering: false,
      scanning: false,
      results: true,
    });

    // Progress state
    const [totalDuration] = useState(() => Math.floor(Math.random() * 40) + 40);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [targetProgress, setTargetProgress] = useState(0);

    // Individual card progress states for loading bars

    const scanMessagesRef = useRef<HTMLDivElement>(null);
    const llmThoughtsRef = useRef<HTMLDivElement>(null);
    const { sessionId } = useSearch({
      from: processRoute.id,
    });
    const navigate = useNavigate();

    const { data: researchProgress } = useResearchProgress(sessionId || null);

    // Calculate target progress based on message rendering completion
    useEffect(() => {
      if (currentStage === "results") {
        setTargetProgress(100);
        return;
      }

      let progress = 0;

      if (currentStage === "gathering" && researchProgress?.gathering) {
        // Gathering stage contributes 40% of total progress
        const totalGatheringMessages = researchProgress.gathering.length;
        const currentGatheringMessages = llmThoughts.length;
        progress = (currentGatheringMessages / totalGatheringMessages) * 40;
      } else if (currentStage === "scanning" && researchProgress?.scanning) {
        // Start with 40% from completed gathering stage
        progress = 40;
        // Scanning stage contributes remaining 60% of total progress
        const totalScanningMessages = researchProgress.scanning.length;
        const currentScanningMessages = scanMessages.length;
        progress += (currentScanningMessages / totalScanningMessages) * 60;
      } else if (stageCompleted.gathering && currentStage === "scanning") {
        // If gathering is complete but scanning hasn't started, we're at 40%
        progress = 40;
      }

      setTargetProgress(progress);
    }, [
      currentStage,
      llmThoughts.length,
      scanMessages.length,
      researchProgress,
      stageCompleted.gathering,
    ]);

    // Smooth progress animation towards target
    useEffect(() => {
      const progressInterval = setInterval(() => {
        setCurrentProgress((prev) => {
          const difference = targetProgress - prev;
          if (Math.abs(difference) < 0.1) {
            return targetProgress;
          }
          // Smooth animation - move 10% of the way to target each update
          return prev + difference * 0.1;
        });
      }, 50); // Update every 50ms for smooth animation

      return () => clearInterval(progressInterval);
    }, [targetProgress]);

    // Stage 1: Gathering data
    useEffect(() => {
      if (currentStage !== "gathering" || !researchProgress?.gathering) return;

      const llmThoughtsMessages = researchProgress?.gathering.map((item) => {
        return `[${item.tag.toUpperCase()}] ${item.message}`;
      });

      // Calculate timing for gathering stage - space messages to use 40% of totalDuration
      const gatheringDuration = totalDuration * 0.4 * 1000; // 40% of total time in milliseconds
      const messageInterval = gatheringDuration / llmThoughtsMessages.length;

      // Start LLM thinking
      setIsLlmThinking(true);

      const thinkingInterval = setInterval(() => {
        setLlmThoughts((prev) => {
          if (prev.length >= llmThoughtsMessages.length) {
            setIsLlmThinking(false);
            setStageCompleted((prevStage) => ({
              ...prevStage,
              gathering: true,
            }));

            // Move to scanning stage immediately (no extra delay)
            setTimeout(() => {
              setCurrentStage("scanning");
            }, 100);

            clearInterval(thinkingInterval);
            return prev; // Don't add any more messages
          }

          // Add the next message based on current array length
          const nextMessage = llmThoughtsMessages[prev.length];

          // When we encounter a locked message, complete current box
          if (nextMessage.includes("[LOCKED]")) {
            setChecklist((prev) => [...prev, true]);
          }
          return [
            ...prev,
            {
              id: `thought-${prev.length}`,
              text: nextMessage,
              timestamp: Date.now(),
            },
          ];
        });
      }, messageInterval);

      return () => clearInterval(thinkingInterval);
    }, [currentStage, totalDuration, researchProgress?.gathering]);

    // Stage 2: Scanning
    useEffect(() => {
      if (currentStage !== "scanning" || !researchProgress?.scanning) return;

      const scanningMessagesArray = researchProgress?.scanning.map((item) => {
        return `[${item.tag.toUpperCase()}] ${item.message}`;
      });

      // Calculate timing for scanning stage - space messages to use 60% of totalDuration
      const scanningDuration = totalDuration * 0.6 * 1000; // 60% of total time in milliseconds
      const messageInterval = scanningDuration / scanningMessagesArray.length;

      setIsLlmThinking(true);

      const scanInterval = setInterval(() => {
        setScanMessages((prev) => {
          // Check if we've added all messages
          if (prev.length >= scanningMessagesArray.length) {
            setIsLlmThinking(false);
            setStageCompleted((prevStage) => ({
              ...prevStage,
              scanning: true,
            }));

            // Move to results stage immediately (no extra delay)
            setTimeout(() => {
              setCurrentStage("results");
            }, 100);

            clearInterval(scanInterval);
            return prev; // Don't add any more messages
          }

          // Add the next message based on current array length
          const nextMessage = scanningMessagesArray[prev.length];

          return [
            ...prev,
            {
              id: `scan-${prev.length}`,
              text: nextMessage,
              timestamp: Date.now(),
            },
          ];
        });
      }, messageInterval);

      return () => clearInterval(scanInterval);
    }, [currentStage, totalDuration, researchProgress?.scanning]);

    // Stage 3: Results
    useEffect(() => {
      if (currentStage !== "results") return;

      const resultMetrics = [
        {
          id: "1",
          label: "📈 Risks",
          value:
            researchProgress.results.high +
            researchProgress.results.medium +
            researchProgress.results.low,
          color: "red" as const,
        },
        {
          id: "2",
          label: "🚫 High",
          value: researchProgress.results.high,
          color: "red" as const,
        },
        {
          id: "3",
          label: "⚠️ Medium",
          value: researchProgress.results.medium,
          color: "yellow" as const,
        },
        {
          id: "4",
          label: "✅ Low",
          value: researchProgress.results.low,
          color: "green" as const,
        },
        {
          id: "5",
          label: "⏱️ Time",
          value: `${Math.floor(totalDuration / 60) > 0 ? `${Math.floor(totalDuration / 60)} min ` : ""}${
            totalDuration % 60
          } sec`,
          color: "green" as const,
        },
      ];

      setResults(resultMetrics);
      setStageCompleted((prev) => ({ ...prev, results: true }));
    }, [currentStage]);

    // Auto-scroll scan messages
    useEffect(() => {
      if (scanMessagesRef.current) {
        scanMessagesRef.current.scrollTop =
          scanMessagesRef.current.scrollHeight;
      }
    }, [scanMessages]);

    // Auto-scroll LLM thoughts
    useEffect(() => {
      if (llmThoughtsRef.current) {
        llmThoughtsRef.current.scrollTop = llmThoughtsRef.current.scrollHeight;
      }
    }, [llmThoughts]);

    const getStageTitle = () => {
      switch (currentStage) {
        case "gathering":
          return "Reviewing Public Regulatory and Brand Data";
        case "scanning":
          return "Live Scan Feed";
        case "results":
          return "Scan Complete";
        default:
          return "Processing";
      }
    };

    const getStageIcon = () => {
      switch (currentStage) {
        case "gathering":
          return <FileMagnifyingGlassIcon size={20} />;
        case "scanning":
          return <CellSignalFullIcon size={20} />;
        case "results":
          return <ChartBarIcon size={20} />;
        default:
          return null;
      }
    };

    const getProgressPercentage = () => {
      return Math.round(currentProgress);
    };

    return (
      <div className="min-h-screen bg-puntt-neutral-gray-3 px-4 py-8">
        <div className="mx-auto w-[752px] space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Logo className="size-10" />
              <Grid>
                <Text size="3" weight="medium">
                  Puntt.ai
                </Text>
                <Text size="3" weight="medium">
                  Marketing Compliance Scanner
                </Text>
              </Grid>
            </div>
            <div className="min-w-48 text-right">
              <div className="mb-2 flex items-center justify-between">
                <p
                  className="text-gray-900"
                  style={{
                    fontFamily: "National 2",
                    fontWeight: 400,
                    fontStyle: "Regular",
                    fontSize: "18px",
                    lineHeight: "26px",
                    letterSpacing: "-0.04px",
                  }}
                >
                  {currentStage === "gathering" && "Initializing"}
                  {currentStage === "scanning" && "Scanning"}
                  {currentStage === "results" && "Scan Complete"}
                </p>
                <Text
                  style={{
                    fontFamily: "National 2",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "18px",
                    lineHeight: "26px",
                    letterSpacing: "-0.04px",
                    textAlign: "right",
                    color: "#3321DA",
                  }}
                >
                  {getProgressPercentage()}%
                </Text>
              </div>
              <div
                className="w-full"
                style={{
                  width: "359px",
                  height: "8px",
                  borderRadius: "25px",
                  border: "1px solid #DDDDDA",
                  background: `linear-gradient(90deg, #3321DA ${getProgressPercentage()}%, rgba(51, 33, 218, 0) ${getProgressPercentage()}%)`,
                }}
              />
            </div>
          </div>

          {/* Stage 1: Gathering Data */}
          {currentStage === "gathering" && !stageCompleted.gathering && (
            <div className="rounded-lg bg-base-white shadow-sm transition-all duration-500">
              <Grid className="p-6" gap="4">
                <Flex align="center" gap="3">
                  <FileMagnifyingGlassIcon
                    size={32}
                    weight="regular"
                    format="outline"
                    className="text-base-black"
                  />
                  <Text
                    className="section-header text-gray-900"
                    data-testid="gathering-section-header"
                  >
                    {getStageTitle()}
                  </Text>
                </Flex>

                {currentStage === "gathering" && (
                  <p className="text-gray-600">
                    Here&apos;s what I&apos;ve looked at so far:
                  </p>
                )}

                {/* Connected Sources */}
                <Flex gap="4" align="center">
                  <Grid
                    className={cx(
                      "relative flex-1 overflow-hidden rounded-lg border p-4 transition-all duration-300",
                      {
                        "bg-base-white": !checklist[0],
                        "border-[#65BA75] bg-[#EBF9EB]": checklist[0],
                        "animate-pulse-scale": !checklist[0],
                      },
                    )}
                    gap="2"
                  >
                    <Flex align="center" gap="2">
                      {!checklist[0] ? (
                        <ClipboardIcon size={16} className="text-base-black" />
                      ) : (
                        <CheckCircleIcon
                          size={16}
                          color="#65BA75"
                          weight="fill"
                        />
                      )}
                      <Text size="3" weight="medium">
                        Brand Rules
                      </Text>
                    </Flex>

                    <Text size="2" weight="regular">
                      Style guide & standards
                    </Text>
                  </Grid>
                  <Grid
                    className={cx(
                      "relative flex-1 overflow-hidden rounded-lg border p-4 transition-all duration-300",
                      {
                        "bg-base-white": !checklist[1],
                        "border-[#65BA75] bg-[#EBF9EB]": checklist[1],
                        "animate-pulse-scale": checklist[0] && !checklist[1],
                      },
                    )}
                    gap="2"
                  >
                    <Flex align="center" gap="2">
                      {!checklist[1] ? (
                        <ScalesIcon size={16} className="text-base-black" />
                      ) : (
                        <CheckCircleIcon
                          size={16}
                          color="#65BA75"
                          weight="fill"
                        />
                      )}
                      <Text size="3" weight="medium">
                        Compliance Rulebook
                      </Text>
                    </Flex>

                    <Text size="2" weight="regular">
                      Legal & regulatory
                    </Text>
                  </Grid>
                  <Grid
                    className={cx(
                      "relative flex-1 overflow-hidden rounded-lg border p-4 transition-all duration-300",
                      {
                        "bg-base-white": !checklist[2],
                        "border-[#65BA75] bg-[#EBF9EB]": checklist[2],
                        "animate-pulse-scale": checklist[1] && !checklist[2],
                      },
                    )}
                    gap="2"
                  >
                    <Flex align="center" gap="2">
                      {!checklist[2] ? (
                        <PaletteIcon size={16} className="text-base-black" />
                      ) : (
                        <CheckCircleIcon
                          size={16}
                          color="#65BA75"
                          weight="fill"
                        />
                      )}
                      <Text size="3" weight="medium">
                        Public Websites
                      </Text>
                    </Flex>

                    <Text size="2" weight="regular">
                      Live campaigns & content
                    </Text>
                  </Grid>
                </Flex>

                {/* LLM Thinking Box */}
                {currentStage === "gathering" && (
                  <div className="font-mono text-gray-800 rounded-lg bg-puntt-neutral-gray-2 p-4 text-sm">
                    <Grid
                      ref={llmThoughtsRef}
                      className="max-h-64 space-y-1 overflow-y-auto"
                    >
                      {llmThoughts.map((thought) => {
                        return (
                          <ThinkingMessage
                            text={thought.text}
                            key={thought.id}
                          />
                        );
                      })}
                      {isLlmThinking && (
                        <div className="flex items-center space-x-2">
                          <div className="bg-blue-500 size-2 animate-pulse rounded-full"></div>
                          <span className="text-gray-600">Thinking...</span>
                        </div>
                      )}
                    </Grid>
                  </div>
                )}
              </Grid>
            </div>
          )}

          {/* Gathering Completed Summary */}
          {stageCompleted.gathering && (
            <div className="rounded-lg bg-base-white px-6 py-4 shadow-sm transition-all duration-500">
              <Flex gap="4" align="center">
                <Text size="4" weight="medium">
                  Public Sources
                </Text>
                <Badge color="grass" variant="soft">
                  <Flex align="center" gap="2">
                    <ClipboardIcon size={14} className="text-[#65BA75]" />
                    <Text size="2" weight="medium">
                      Brand Rules
                    </Text>
                  </Flex>
                </Badge>
                <Badge color="grass" variant="soft">
                  <Flex align="center" gap="2">
                    <ScalesIcon size={14} className="text-[#65BA75]" />
                    <Text size="2" weight="medium">
                      Compliance Rulebook
                    </Text>
                  </Flex>
                </Badge>
                <Badge color="grass" variant="soft">
                  <Flex align="center" gap="2">
                    <PaletteIcon size={14} className="text-[#65BA75]" />
                    <Text size="2" weight="medium">
                      Public Websites
                    </Text>
                  </Flex>
                </Badge>
              </Flex>
            </div>
          )}

          {/* Stage 2: Scanning */}
          {currentStage === "scanning" && !stageCompleted.scanning && (
            <div className="rounded-lg bg-base-white shadow-sm transition-all duration-500">
              <div className="p-6">
                <Flex align="center" justify="between" className="mb-4">
                  <Flex align="center" gap="3">
                    <CellTowerIcon
                      size={32}
                      className="text-base-black"
                      format="outline"
                    />
                    <Text
                      data-testid="live-scan-section-header"
                      size="7"
                      weight="medium"
                    >
                      Live Scan Feed
                    </Text>
                  </Flex>

                  <Callout.Root
                    className="max-w-[402px] border border-puntt-neutral-gray-9 text-base-black"
                    size="1"
                  >
                    <Callout.Text size="1">
                      <b>Here’s where I’m looking:</b> Doing a quick review of
                      your public websites and regulatory databases for the
                      latest regulatory info.
                    </Callout.Text>
                  </Callout.Root>
                </Flex>

                {/* Scan Messages */}
                <Grid
                  ref={scanMessagesRef}
                  className="font-mono max-h-64 space-y-2 overflow-y-auto rounded-lg bg-puntt-neutral-gray-2 p-4 text-sm"
                >
                  {scanMessages.map((message) => {
                    return (
                      <ThinkingMessage text={message.text} key={message.id} />
                    );
                  })}
                  {currentStage === "scanning" && isLlmThinking && (
                    <div className="flex items-center space-x-2">
                      <div className="bg-blue-500 size-2 animate-pulse rounded-full"></div>
                      <span className="text-gray-600">Processing...</span>
                    </div>
                  )}
                </Grid>
              </div>
            </div>
          )}

          {/* Scanning Completed Summary */}
          {stageCompleted.scanning && (
            <div className="rounded-lg bg-base-white px-6 py-4 shadow-sm transition-all duration-500">
              <Flex gap="4" align="center">
                <Text size="4" weight="medium">
                  Scan Complete
                </Text>
              </Flex>
            </div>
          )}

          {/* Stage 3: Results */}
          {currentStage === "results" && (
            <div className="rounded-lg bg-base-white px-6 py-4 shadow-sm transition-all duration-500">
              <div className="p-6">
                <Flex align="center" gap="3" className="mb-4">
                  <ChartBarIcon
                    size={32}
                    className="text-base-black"
                    format="outline"
                  />
                  <Text
                    className="section-header text-gray-900"
                    data-testid="scan-results-section-header"
                  >
                    Scan Results
                  </Text>
                </Flex>

                <div className="mb-6">
                  {/* Metrics */}
                  <Flex gap="4" align="center" className="mb-6">
                    {results.map((metric, i) => (
                      <Flex key={metric.id} align="center" gap="2">
                        <Text size="4" weight="regular">
                          {metric.label}: {metric.value}
                        </Text>
                        {i < results.length - 1 && (
                          <Text size="4" weight="regular">
                            |
                          </Text>
                        )}
                      </Flex>
                    ))}
                  </Flex>

                  {/* Results Summary */}
                  <Grid className="space-y-2 rounded-lg bg-puntt-neutral-gray-2 p-4 text-sm">
                    <ThinkingMessage
                      text={`[INFO] All sources gathered and ${Math.floor(Math.random() * (80 - 50 + 1)) + 50} public resources checked`}
                    />
                    <ThinkingMessage
                      text={`[INFO] Scan completed in  ${totalDuration} seconds`}
                    />
                    <ThinkingMessage
                      text={`[INFO] ${
                        researchProgress.results.high +
                        researchProgress.results.medium +
                        researchProgress.results.low
                      } risk violations`}
                    />
                    <ThinkingMessage
                      text={`[ALERT] ${researchProgress?.results.high} high-priority risks worth checking first.`}
                    />
                  </Grid>
                </div>

                {/* CTA Button */}
                <Button
                  className="w-full"
                  size="3"
                  onClick={() => {
                    navigate({
                      to: resultsRoute.to,
                      search: { sessionId },
                      replace: true,
                    });
                  }}
                >
                  <CheckIcon size={20} className="mr-2" />
                  Review Risks
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
});
